<template>
    <Page class="activity-metrics-page">
        <template #title>
            <h1>Organization Activity Insights</h1>
        </template>
        <template #controls>
            <SelectButton
                v-model="selectedPeriod"
                :options="periodOptions"
                optionLabel="label"
                optionValue="value"
                @change="onPeriodChange"
            />
            <Button
                icon="pi pi-refresh"
                label="Refresh"
                severity="secondary"
                @click="refreshMetrics"
            />
        </template>

        <div v-if="isLoading" class="loading-container">
            <Spinner />
        </div>

        <Message v-else-if="currentError" severity="error">
            {{ currentError }}
        </Message>

        <div v-else-if="activityReport" class="metrics-content">
            <!-- Summary Section -->
            <Section>
                <template #title
                    >Summary (Last
                    {{ activityReport.time_period_days }} Days)</template
                >
                <div class="summary-grid">
                    <Card>
                        <template #title>Total Organizations</template>
                        <template #content>
                            <div class="metric-value">
                                {{ activityReport.organizations.length }}
                            </div>
                        </template>
                    </Card>

                    <Card>
                        <template #title>Active Organizations</template>
                        <template #content>
                            <div class="metric-value">
                                {{ activeOrganizationsCount }}
                            </div>
                            <div class="metric-detail">
                                {{ activeOrganizationsPercentage }}% have
                                activity
                            </div>
                        </template>
                    </Card>

                    <Card>
                        <template #title>Total Users</template>
                        <template #content>
                            <div class="metric-value">
                                {{ totalUsersCount }}
                            </div>
                        </template>
                    </Card>

                    <Card>
                        <template #title>Total Auth Events</template>
                        <template #content>
                            <div class="metric-value">
                                {{ totalAuthEventsCount }}
                            </div>
                        </template>
                    </Card>
                </div>
            </Section>

            <!-- Organizations Table -->
            <Section>
                <template #title>Organizations Activity</template>
                <DataTable
                    :value="activityReport.organizations"
                    v-model:expandedRows="expandedRows"
                    dataKey="organization_id"
                    :paginator="activityReport.organizations.length > 10"
                    :rows="10"
                    :rowsPerPageOptions="[10, 25, 50]"
                    sortField="last_activity_at"
                    :sortOrder="1"
                >
                    <Column expander style="width: 3rem" />

                    <Column
                        field="organization_name"
                        header="Organization"
                        sortable
                    />

                    <Column field="total_users" header="Total Users" sortable />

                    <Column
                        field="active_users_in_period"
                        header="Active Users"
                        sortable
                    >
                        <template #body="{ data }">
                            <span>{{ data.active_users_in_period }}</span>
                            <span class="percentage"
                                >({{ getActiveUserPercentage(data) }}%)</span
                            >
                        </template>
                    </Column>

                    <Column
                        field="total_auth_events_in_period"
                        header="Auth Events"
                        sortable
                    />

                    <Column
                        field="last_activity_at"
                        header="Last Activity"
                        sortable
                    >
                        <template #body="{ data }">
                            <span v-if="data.last_activity_at">
                                {{ formatDateTime(data.last_activity_at) }}
                            </span>
                            <span v-else class="no-activity">No activity</span>
                        </template>
                    </Column>

                    <Column header="Days Inactive" sortable>
                        <template #body="{ data }">
                            <Tag
                                v-if="data.last_activity_at"
                                :value="getDaysInactive(data.last_activity_at)"
                                :severity="
                                    getInactiveSeverity(data.last_activity_at)
                                "
                            />
                            <span v-else>N/A</span>
                        </template>
                    </Column>

                    <!-- Expandable row for user details -->
                    <template #expansion="{ data }">
                        <div class="user-details-expansion">
                            <h3>Users in {{ data.organization_name }}</h3>
                            <DataTable
                                :value="data.user_activities"
                                dataKey="user_id"
                                :paginator="data.user_activities.length > 5"
                                :rows="5"
                            >
                                <Column field="email" header="Email" />
                                <Column field="name" header="Name" />
                                <Column
                                    field="auth_count_in_period"
                                    header="Auth Count"
                                />
                                <Column
                                    field="last_activity_at"
                                    header="Last Activity"
                                >
                                    <template #body="{ data: userData }">
                                        <span v-if="userData.last_activity_at">
                                            {{
                                                formatDateTime(
                                                    userData.last_activity_at
                                                )
                                            }}
                                        </span>
                                        <span v-else class="no-activity"
                                            >Never</span
                                        >
                                    </template>
                                </Column>
                            </DataTable>
                        </div>
                    </template>
                </DataTable>
            </Section>
        </div>
    </Page>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import Card from "primevue/card";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import Message from "primevue/message";
import SelectButton from "primevue/selectbutton";
import Tag from "primevue/tag";
import { computed, onMounted, ref } from "vue";

import Page from "@/components/Page.vue";
import Section from "@/components/Section.vue";
import Spinner from "@/shared/Spinner.vue";
import {
    OrganizationActivity,
    useActivityMetricsManager,
    useActivityMetricsStore,
} from "@/stores/activity-metrics-store";

const activityMetricsStore = useActivityMetricsStore();
const { activityReport, isLoading, currentError } = useActivityMetricsManager();

const selectedPeriod = ref(30);
const periodOptions = [
    { label: "7 Days", value: 7 },
    { label: "30 Days", value: 30 },
    { label: "60 Days", value: 60 },
    { label: "90 Days", value: 90 },
];

const expandedRows = ref({});

onMounted(async () => {
    await activityMetricsStore.getActivityReport(selectedPeriod.value);
});

const onPeriodChange = async () => {
    await activityMetricsStore.getActivityReport(selectedPeriod.value);
};

const refreshMetrics = async () => {
    await activityMetricsStore.getActivityReport(selectedPeriod.value);
};

// Computed metrics
const activeOrganizationsCount = computed(() => {
    if (!activityReport.value) return 0;
    return activityReport.value.organizations.filter(
        (org) => org.active_users_in_period > 0
    ).length;
});

const activeOrganizationsPercentage = computed(() => {
    if (
        !activityReport.value ||
        activityReport.value.organizations.length === 0
    )
        return 0;
    return Math.round(
        (activeOrganizationsCount.value /
            activityReport.value.organizations.length) *
            100
    );
});

const totalUsersCount = computed(() => {
    if (!activityReport.value) return 0;
    return activityReport.value.organizations.reduce(
        (sum, org) => sum + org.total_users,
        0
    );
});

const totalAuthEventsCount = computed(() => {
    if (!activityReport.value) return 0;
    return activityReport.value.organizations.reduce(
        (sum, org) => sum + org.total_auth_events_in_period,
        0
    );
});

// Helper functions
const getActiveUserPercentage = (org: OrganizationActivity) => {
    if (org.total_users === 0) return 0;
    return Math.round((org.active_users_in_period / org.total_users) * 100);
};

const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
};

const getDaysInactive = (lastActivityString: string) => {
    const lastActivity = new Date(lastActivityString);
    const now = new Date();
    const diffDays = Math.floor(
        (now.getTime() - lastActivity.getTime()) / (1000 * 60 * 60 * 24)
    );
    return `${diffDays} days`;
};

const getInactiveSeverity = (lastActivityString: string) => {
    const lastActivity = new Date(lastActivityString);
    const now = new Date();
    const diffDays = Math.floor(
        (now.getTime() - lastActivity.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffDays < 7) return "success";
    if (diffDays < 30) return "info";
    if (diffDays < 60) return "warn";
    return "danger";
};
</script>

<style scoped lang="scss">
.activity-metrics-page {
    padding: 2rem;
    max-width: 1600px;
    margin: 0 auto;
}

.metrics-content {
    display: flex;
    flex-direction: column;
    gap: 2rem;
}

.summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
}

.metric-value {
    font-size: 2.5rem;
    font-weight: bold;
    color: var(--primary-color);
}

.metric-detail {
    font-size: 0.9rem;
    color: var(--text-color-secondary);
    margin-top: 0.5rem;
}

.percentage {
    margin-left: 0.5rem;
    color: var(--text-color-secondary);
    font-size: 0.9em;
}

.no-activity {
    color: var(--red-500);
    font-style: italic;
}

.user-details-expansion {
    padding: 1rem 2rem;
    background-color: var(--surface-ground);

    h3 {
        margin-bottom: 1rem;
        color: var(--text-color);
    }
}

.loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
}
</style>
